import BigNumber from 'bignumber.js';
import type { Address, PublicClient } from 'viem';

import { xvsTokenOmnichainAbi } from 'libs/contracts';

type MulticallResult<T> =
  | {
      status: 'success';
      result: T;
    }
  | {
      status: 'failure';
    };

const unwrapMulticallResult = <T>(result: MulticallResult<T>) => {
  if (result.status === 'failure') {
    throw new Error('Failed to fetch XVS bridge mint status');
  }

  return result.result;
};

export interface GetXvsMintStatusInput {
  chainXvsProxyOftDestContractAddress: Address;
  xvsTokenOmnichainContractAddress: Address;
  publicClient: PublicClient;
}

export interface GetXvsMintStatusOutput {
  minterToCapMantissa: BigNumber;
  bridgeAmountMintedMantissa: BigNumber;
}

export const getXvsBridgeMintStatus = async ({
  chainXvsProxyOftDestContractAddress,
  xvsTokenOmnichainContractAddress,
  publicClient,
}: GetXvsMintStatusInput): Promise<GetXvsMintStatusOutput> => {
  const [minterToCapResult, bridgeAmountMintedResult] = (await publicClient.multicall({
    contracts: [
      {
        address: xvsTokenOmnichainContractAddress,
        abi: xvsTokenOmnichainAbi,
        functionName: 'minterToCap',
        args: [chainXvsProxyOftDestContractAddress],
      },
      {
        address: xvsTokenOmnichainContractAddress,
        abi: xvsTokenOmnichainAbi,
        functionName: 'minterToMintedAmount',
        args: [chainXvsProxyOftDestContractAddress],
      },
    ],
  })) as unknown as [MulticallResult<bigint>, MulticallResult<bigint>];

  const minterToCap = unwrapMulticallResult(minterToCapResult);
  const bridgeAmountMinted = unwrapMulticallResult(bridgeAmountMintedResult);

  return {
    minterToCapMantissa: new BigNumber(minterToCap.toString()),
    bridgeAmountMintedMantissa: new BigNumber(bridgeAmountMinted.toString()),
  };
};
